import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'

import { trackEvent } from '@/shared/lib/analytics'
import { notify } from '@/shared/lib/notify'

import { sessionsApi } from '../api/sessionsApi'
import type {
  ChangeStatusRequest,
  CreateSessionRequest,
  SessionDetail,
  SessionSearchParams,
  UpdateSessionRequest,
} from '../types/session.types'

import { mySessionsKeys } from './useMySessions'

/**
 * Query keys centralizadas para que las invalidaciones sean consistentes.
 * Patrón inspirado en https://tkdodo.eu/blog/effective-react-query-keys
 */
export const sessionKeys = {
  all: ['sessions'] as const,
  lists: () => [...sessionKeys.all, 'list'] as const,
  list: (params: SessionSearchParams) => [...sessionKeys.lists(), params] as const,
  details: () => [...sessionKeys.all, 'detail'] as const,
  // Include the current locale so a language switch invalidates the cached
  // response (backend returns the summary in the active language).
  detail: (id: number) => [...sessionKeys.details(), id, i18next.language] as const,
  players: (id: number) => [...sessionKeys.detail(id), 'players'] as const,
}

/** Listado paginado y filtrado. Público. */
export function useSessionsQuery(params: SessionSearchParams = {}) {
  return useQuery({
    queryKey: sessionKeys.list(params),
    queryFn: () => sessionsApi.search(params),
    staleTime: 60_000, // 1 min — listado se refresca rápido pero no en cada navegación
  })
}

/**
 * Próximas N sesiones joinable. Público. Usado por el CommunityCarousel
 * de la landing — staleTime largo (5 min) porque cambia poco y la landing
 * se vuelve a montar al cambiar de ruta.
 */
export function useUpcomingSessionsQuery(limit = 5) {
  return useQuery({
    queryKey: [...sessionKeys.all, 'upcoming', limit] as const,
    queryFn: () => sessionsApi.upcoming(limit),
    staleTime: 5 * 60_000,
    retry: 0,
  })
}

/** Detalle de una sesión. */
export function useSessionDetailQuery(id: number | undefined) {
  return useQuery({
    queryKey: id ? sessionKeys.detail(id) : ['sessions', 'detail', 'pending'],
    queryFn: () => sessionsApi.getById(id!),
    enabled: typeof id === 'number',
    staleTime: 30_000,
  })
}

/**
 * Source-de-duplicación de una sesión propia. Endpoint owner-only —
 * permite al creator rellenar el form de "Duplicar" desde cualquiera de
 * sus partidas (incluido el histórico de COMPLETED / CANCELLED), sin la
 * regla de visibilidad pública de {@link useSessionDetailQuery}.
 */
export function useDuplicateSourceQuery(id: number | undefined) {
  return useQuery({
    queryKey: id
      ? ['sessions', 'duplicate-source', id]
      : ['sessions', 'duplicate-source', 'pending'],
    queryFn: () => sessionsApi.getDuplicateSource(id!),
    enabled: typeof id === 'number',
    staleTime: 30_000,
  })
}

/** Listado de jugadores aislado (útil para polling/refresh ligero). */
export function useSessionPlayersQuery(id: number | undefined) {
  return useQuery({
    queryKey: id ? sessionKeys.players(id) : ['sessions', 'players', 'pending'],
    queryFn: () => sessionsApi.listPlayers(id!),
    enabled: typeof id === 'number',
    staleTime: 15_000,
  })
}

// ---------- Mutations ----------

/**
 * Actualiza la cache de detail con la respuesta más reciente y la lista
 * (que pueda contener esta sesión).
 */
function syncCacheFromDetail(qc: ReturnType<typeof useQueryClient>, detail: SessionDetail) {
  qc.setQueryData(sessionKeys.detail(detail.id), detail)
  // El listado puede tener filtros distintos — invalidamos todos los lists.
  // invalidateQueries devuelve Promise pero no esperamos: fire-and-forget OK aquí.
  void qc.invalidateQueries({ queryKey: sessionKeys.lists() })
  void qc.invalidateQueries({ queryKey: sessionKeys.players(detail.id) })
  // "Mis partidas" tiene su propio cache (useMySessionsQuery). Sin esto,
  // al salir/entrar/cambiar estado, la lista mostraba el estado viejo
  // (p.ej. sesión a la que el user se acaba de salir sigue apareciendo
  // bajo el tab "Apuntadas") hasta el próximo refetch natural.
  void qc.invalidateQueries({ queryKey: mySessionsKeys.all })
  // Cualquier cambio en una partida puede alterar la ocupación de mesas de un
  // local: vincular/desvincular local (reservar/soltar), o cancelar/completar
  // (libera la mesa). Refresca la disponibilidad PÚBLICA (ficha, directorio,
  // zona) para que "N de M libres" no quede desincronizado. Prefijo `stores`
  // (plural) = solo las públicas, no las del owner (`store`, singular).
  void qc.invalidateQueries({ queryKey: ['stores'] })
}

export function useCreateSessionMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateSessionRequest) => sessionsApi.create(body),
    onSuccess: (detail) => {
      syncCacheFromDetail(qc, detail)
      trackEvent('Session Created')
    },
  })
}

export function useUpdateSessionMutation(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateSessionRequest) => sessionsApi.update(id, body),
    onSuccess: (detail) => syncCacheFromDetail(qc, detail),
  })
}

export function useChangeSessionStatusMutation(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChangeStatusRequest) => sessionsApi.changeStatus(id, body),
    onSuccess: (detail) => syncCacheFromDetail(qc, detail),
  })
}

/**
 * Optimistic update sobre el cache `detail` (el contexto que el usuario está
 * mirando al pulsar Unirme/Salir). Patrón hot-list CLAUDE.md: cancel + snapshot
 * + update + rollback. Solo tocamos `detail` optimistamente; listas y
 * "mis partidas" se reconcilian con la respuesta autoritativa en `onSuccess`
 * (`syncCacheFromDetail`), así que NO necesitan snapshot/rollback aquí.
 */
async function optimisticDetailUpdate(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  apply: (prev: SessionDetail) => SessionDetail,
) {
  const key = sessionKeys.detail(id)
  await qc.cancelQueries({ queryKey: key })
  const previous = qc.getQueryData<SessionDetail>(key)
  if (previous) {
    qc.setQueryData<SessionDetail>(key, apply(previous))
  }
  return { key, previous }
}

function rollbackDetail(
  qc: ReturnType<typeof useQueryClient>,
  ctx: { key: readonly unknown[]; previous: SessionDetail | undefined } | undefined,
) {
  if (ctx?.previous) {
    qc.setQueryData(ctx.key, ctx.previous)
  }
}

export function useJoinSessionMutation(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sessionsApi.join(id),
    onMutate: () =>
      optimisticDetailUpdate(qc, id, (prev) => {
        // Si ya estás dentro (yourRole != null) no tocamos nada.
        if (prev.yourRole) return prev
        const seatFree = prev.registeredPlayers < prev.maxPlayers
        return {
          ...prev,
          yourRole: seatFree ? 'PLAYER' : 'WAITLIST',
          registeredPlayers: seatFree ? prev.registeredPlayers + 1 : prev.registeredPlayers,
          waitlistCount: seatFree ? prev.waitlistCount : prev.waitlistCount + 1,
        }
      }),
    onError: (_err, _vars, ctx) => rollbackDetail(qc, ctx),
    onSuccess: (detail) => {
      syncCacheFromDetail(qc, detail)
      // Conversión: distinguimos plaza directa vs lista de espera por prop.
      trackEvent('Session Joined', { role: detail.yourRole ?? 'PLAYER' })
      notify.success(
        detail.yourRole === 'WAITLIST'
          ? i18next.t('toasts.joinedWaitlist')
          : i18next.t('toasts.joinedPlayer'),
      )
    },
  })
}

export function useLeaveSessionMutation(id: number) {
  const qc = useQueryClient()
  return useMutation({
    // `quiet`: sin el toast de "has salido" — la salida de la cola ya avisó con el
    // toast de «Deshacer» (runUndoable) y no queremos dos avisos seguidos.
    mutationFn: (_opts: { quiet?: boolean } | void) => sessionsApi.leave(id),
    onMutate: () =>
      optimisticDetailUpdate(qc, id, (prev) => {
        if (!prev.yourRole) return prev
        const wasPlayer = prev.yourRole === 'PLAYER'
        return {
          ...prev,
          yourRole: null,
          registeredPlayers: wasPlayer
            ? Math.max(0, prev.registeredPlayers - 1)
            : prev.registeredPlayers,
          waitlistCount: wasPlayer ? prev.waitlistCount : Math.max(0, prev.waitlistCount - 1),
        }
      }),
    onError: (_err, _vars, ctx) => rollbackDetail(qc, ctx),
    onSuccess: (detail, opts) => {
      syncCacheFromDetail(qc, detail)
      if (!(opts && opts.quiet)) notify.success(i18next.t('toasts.left'))
    },
  })
}

/**
 * Mutation para que el creator expulse a un participante. Acepta el
 * userId del expulsado como parámetro de la mutate(). El detail
 * que devuelve el backend ya viene con waitlist promocionado +
 * status recalculado, así que basta con syncCacheFromDetail.
 */
export function useKickPlayerMutation(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => sessionsApi.kickPlayer(id, userId),
    onSuccess: (detail) => syncCacheFromDetail(qc, detail),
  })
}

/** Check-in de asistencia de un player (organizador de evento de tienda). */
export function useCheckInMutation(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, checkedIn }: { userId: number; checkedIn: boolean }) =>
      sessionsApi.checkInPlayer(id, userId, checkedIn),
    onSuccess: (detail) => syncCacheFromDetail(qc, detail),
  })
}
